import {
	AppEnumHelper,
	dateCompare,
	DetailItem,
	DetailSection,
	DOMAIN_TYPES,
	Header,
	PermissionEnums,
	sortByLabel,
	USER_ROLES,
} from "@/Shared";

import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";
import { PermissionModel } from "../Models/PermissionModel";
import { AccessRightModel } from "@/Domains/Auth";
import { ResourceAccessViewModel } from "./ResourceAccessViewModel";
import { ResourceAccessHeader } from "../resource-access-config";


export interface PermissionVMParams {
	permission: PermissionModel;
	viewEnums: PermissionEnums;
	accessRight: AccessRightModel;
	isDisplaying: boolean;
}

export class PermissionViewModel {
	public id: number;
	public model: PermissionModel;
	public role: string;
	public resourceAccessList!: ResourceAccessViewModel[];
	public resourceAccessHeaders!: Record<keyof ResourceAccessViewModel, Header<ResourceAccessViewModel>>


	public audit!: AuditViewModel;
	public creationDate!: string;
	public creatorName!: string;

	public cardClass!: string;
	public cardHeaderTitle!: string;


	constructor(protected params: PermissionVMParams) {

		const { permission, viewEnums, isDisplaying } = params;

		this.id = permission?.id ?? 0;
		this.model = permission;
		this.audit = new AuditViewModel(permission as AuditModel);

		this.role = AppEnumHelper.getLabel(viewEnums.UserRoleEnum, permission.role);
		this.initResourceAccess();

		this.creationDate = this.audit.creationDate;
		this.creatorName = this.audit.creatorName;

		this.cardHeaderTitle = `${this.role} - (ID : ${this.id})`;
		this.cardClass = isDisplaying ? 'active' : '';
	}

	public isNotEditable() {
		return this.model?.role === USER_ROLES.SUPER_ADMIN;
	}

	private initResourceAccess() {
		const { permission, viewEnums } = this.params;
		this.resourceAccessHeaders = ResourceAccessHeader.build(viewEnums);
		this.resourceAccessList = (permission.resourceAccessList ?? [])
			.map(ra => new ResourceAccessViewModel(
				this.id,
				this.role,
				ra,
				viewEnums
			)
			).sort(
				(ra1, ra2) => sortByLabel(
					{ label: ra1.resource },
					{ label: ra2.resource }
				)
			);
	}

	get title() {
		return `Permission des ${this.role}s`;
	};

	getAllSections = (): DetailSection[] => {
		const sections: DetailSection[] = [
			{
				sectionFn: this.getPermissionSection,
				legend: "Détails de la Permission",
				type: "box",
			}
		];

		if (this.params.accessRight.canAudit(DOMAIN_TYPES.Permission)) {
			sections.push({
				sectionFn: this.audit.getAuditTrail,
				legend: "Traçabilité",
				type: "box",
			});
		}
		return sections;
	};

	getPermissionSection = (): DetailItem[] => {
		return [
			{ label: "N°", value: String(this.id) },
			{ label: "Rôle", value: this.role },
			{ label: "Res. attributées", value: String(this.resourceAccessList?.length ?? 0) },
			{ label: "Créée le", value: this.creationDate },
			{ label: "Créée par", value: this.creatorName },
		];
	};


	compareTo(other: PermissionViewModel): number {
		return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
	}
}
